import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

const LANGS = ["fr", "nl", "en"] as const;

/**
 * Sélecteur de langue FR / NL / EN.
 *
 * Utilisé sur les écrans auth (AuthLayout) et dans le header V2 (tous les
 * rôles, dont prestataire). `i18n.changeLanguage` persiste le choix en
 * localStorage (cf. lib/i18n.ts). En plus, si l'utilisateur est connecté
 * (vraie session, pas dev), on persiste la langue dans `profiles.lang` —
 * elle le suit alors d'un appareil à l'autre et sert de langue pour ses
 * emails (cf. migration 071).
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n } = useTranslation();
  const { profile, isDevSession, patchProfile } = useAuth();

  const handleChange = (lang: string) => {
    i18n.changeLanguage(lang);
    // Persistance serveur : seulement pour une vraie session connectée (les dev
    // sessions n'ont pas d'auth.uid → rien à persister). Via RPC SECURITY DEFINER
    // (fiable, contrairement à l'UPDATE direct dont les erreurs étaient avalées).
    if (profile?.id && !isDevSession && profile.lang !== lang) {
      patchProfile({ lang });
      supabase.rpc("set_my_lang", { p_lang: lang }).then(({ error }) => {
        if (error) console.warn("[LanguageSwitcher] persistance langue échouée:", error.message);
      });
    }
  };

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {LANGS.map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => handleChange(lang)}
          className={cn(
            "px-2.5 py-1 text-xs uppercase rounded transition-colors cursor-pointer outline-none shadow-none",
            (i18n.language || "").startsWith(lang)
              ? "text-accent font-semibold"
              : "text-text-muted hover:text-text-primary",
          )}
        >
          {lang}
        </button>
      ))}
    </div>
  );
}
